// Compares the skill's stylistic fingerprint to the corpus centroid built
// offline by scripts/build-style-fingerprint.ts. Reports z-scores per metric
// and an overall stylistic distance. Outliers >2σ are flagged.

import fingerprint from '../../../data/styleFingerprint.json';
import type { TextMetrics } from './text';
import type { ArchitectureMetrics } from './architecture';

const METRICS = [
  'descChars',
  'bodyWords',
  'sentenceAvg',
  'imperativeRatio',
  'passiveRatio',
  'hedgeDensity',
  'fillerDensity',
  'uniqueTokenRatio',
  'codeToProse',
  'headingDensity',
  'bulletDensity',
  'decisionDensity',
  'validationDensity',
] as const;
export type StyleMetricKey = typeof METRICS[number];

export interface StyleMetrics {
  zScores: Record<StyleMetricKey, number>;
  outliers: { metric: StyleMetricKey; z: number; direction: 'above' | 'below' }[];
  meanAbsZ: number;
  maxAbsZ: number;
  centroidSampleSize: number;
}

interface FingerprintEntry { mean: number; std: number }

const FP = fingerprint.metrics as Record<StyleMetricKey, FingerprintEntry>;
const SAMPLE = fingerprint.sampleSize as number;

function zScore(value: number, m: FingerprintEntry): number {
  if (m.std <= 0) return 0;
  return (value - m.mean) / m.std;
}

export interface StyleInputs {
  descChars: number;
  bodyText: TextMetrics;
  arch: ArchitectureMetrics;
}

export function analyzeStyle({ descChars, bodyText, arch }: StyleInputs): StyleMetrics {
  const wc = Math.max(1, bodyText.wordCount);
  const values: Record<StyleMetricKey, number> = {
    descChars,
    bodyWords: bodyText.wordCount,
    sentenceAvg: bodyText.avgWordsPerSentence,
    imperativeRatio: bodyText.imperativeRatio,
    passiveRatio: bodyText.passiveRatio,
    hedgeDensity: (bodyText.hedgeCount * 1000) / wc,
    fillerDensity: (bodyText.fillerCount * 1000) / wc,
    uniqueTokenRatio: bodyText.uniqueLowercaseTokenRatio,
    codeToProse: arch.codeToProseRatio,
    headingDensity: (arch.headings.length * 100) / wc,
    bulletDensity: (arch.bulletItems * 100) / wc,
    decisionDensity: (arch.decisionMarkers * 100) / wc,
    validationDensity: (arch.validationMarkers * 100) / wc,
  };

  const zScores = Object.fromEntries(
    METRICS.map((k) => [k, zScore(values[k], FP[k])]),
  ) as Record<StyleMetricKey, number>;

  const outliers = METRICS
    .map((k) => ({ metric: k, z: zScores[k], direction: (zScores[k] >= 0 ? 'above' : 'below') as 'above' | 'below' }))
    .filter((o) => Math.abs(o.z) >= 2);

  const absZs = METRICS.map((k) => Math.abs(zScores[k]));
  const meanAbsZ = absZs.reduce((a, b) => a + b, 0) / absZs.length;
  const maxAbsZ = Math.max(...absZs);

  return { zScores, outliers, meanAbsZ, maxAbsZ, centroidSampleSize: SAMPLE };
}
